export const API_BASE_URL =
  import.meta.env.VITE_API_URL || "/api/v1";

/**
 * Resolve a possibly-relative file URL into an absolute one the
 * browser can fetch.
 *
 * Backend ``StorageBackend._url_for`` SHOULD return absolute URLs
 * already (using ``settings.API_URL``), but in deployments where
 * ``API_URL`` is misconfigured (empty or pointing at localhost), the
 * stored ``avatar_url`` lands as a relative ``/api/v1/files/<key>``
 * path. A relative URL on the SPA host (``app.lenavio.com``) doesn't
 * resolve to the API host (``api.lenavio.com``), so the image loads
 * as a broken link.
 *
 * This helper bridges the gap by prepending the SPA's known API base
 * to anything that smells like a relative API path. Absolute URLs
 * (https://, http://, data:) and non-API paths pass through
 * untouched.
 */
export function resolveFileUrl(src: string | null | undefined): string | null {
  if (!src) return null;
  // Already absolute — http(s) URLs and data: URIs.
  if (/^(https?:|data:)/i.test(src)) return src;
  // Relative API path — prepend the API origin we already know.
  if (src.startsWith("/api/v1/")) {
    // ``API_BASE_URL`` already ends with /api/v1 — strip the dup
    // before joining so we don't get /api/v1/api/v1/...
    const base = API_BASE_URL.replace(/\/api\/v1\/?$/, "");
    return `${base}${src}`;
  }
  // Legacy /uploads/ path — same backend host as the API.
  if (src.startsWith("/uploads/")) {
    const base = API_BASE_URL.replace(/\/api\/v1\/?$/, "");
    return `${base}${src}`;
  }
  // Anything else (data URLs, blob:, custom CDN host) — pass through.
  return src;
}

export const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: "Super Admin",
  HR_ADMIN: "HR Admin",
  MANAGER: "Manager",
  EMPLOYEE: "Employee",
};

export const STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Active",
  INACTIVE: "Inactive",
  TERMINATED: "Terminated",
  ON_LEAVE: "On Leave",
};

export const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  FULL_TIME: "Full-time",
  PART_TIME: "Part-time",
  CONTRACTOR: "Contractor",
  TEMP: "Temporary",
};

export const STATUS_COLORS: Record<string, string> = {
  ACTIVE: "bg-success/10 text-success",
  INACTIVE: "bg-text-muted/10 text-text-muted",
  TERMINATED: "bg-danger/10 text-danger",
  ON_LEAVE: "bg-warning/10 text-warning",
};
